<template>
  <div class="lead-profile-page">
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getLead"
      :editLead="selectedLead"
    />

    <v-alert
      v-if="lead && lead.alert"
      border="left"
      type="error"
      transition="slide-y-transition"
      dismissible
      class="mb-4"
      v-model="lead.alert"
      color="red"
    >
      <strong>{{ lead.note }}</strong>
    </v-alert>

    <v-card v-if="lead" outlined class="lead-profile-card">
      <div class="profile-header">
        <div class="profile-title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="profile-title text-capitalize">{{ lead.name }}</div>
            <div class="profile-subtitle">
              Lead created {{ lead.createdAt | moment("from", "now") }}
            </div>
          </div>
        </div>

        <div class="profile-actions">
          <v-chip small label :color="statusColor(lead.status)" :text-color="statusTextColor(lead.status)">
            {{ lead.status || "Not Set" }}
          </v-chip>

          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon>
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>

            <v-list class="menu-button">
              <v-list-item v-if="checkRightStatus(44)" @click="editLead(lead)">
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="checkRightStatus(45)" @click="deleteLead(lead)">
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12" md="7">
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Contact Name</div>
                <div class="info-value">{{ lead.contactName || "Not Set" }}</div>
              </div>

              <div class="info-item">
                <div class="info-label">Phone Number</div>
                <div class="info-value">
                  <span v-if="lead.phoneNumber">{{ lead.phoneNumber | USPhoneNumber }}</span>
                  <span v-else>Not Set</span>
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">Email</div>
                <div class="info-value">
                  <a v-if="lead.email" @click="sendEmail(lead.email)">{{ lead.email }}</a>
                  <span v-else>Not Set</span>
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">Created At</div>
                <div class="info-value">{{ lead.createdAt | moment("MM/DD/YYYY") }}</div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="5">
            <div class="address-card" v-if="lead.address">
              <div class="info-label">Address</div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <a v-on="on" @click="openGoogleMap">
                    <div class="address-line">{{ lead.address }}</div>
                    <div>{{ lead.city }}, {{ lead.state }} {{ lead.zip }}</div>
                  </a>
                </template>
                <span>Click to open google map</span>
              </v-tooltip>
            </div>

            <div class="address-card" v-else>
              <div class="info-label">Address</div>
              <div class="info-value">Not Set</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div v-else class="loading-box">
      <v-progress-circular size="50" width="5" color="primary" indeterminate />
      <div class="mt-3">Loading lead...</div>
    </div>

    <v-row dense class="mt-4" v-if="lead">
      <v-col cols="12" md="4">
        <v-card outlined class="note-card">
          <div class="section-header">
            <div>
              <div class="section-title">Note</div>
              <div class="section-subtitle">Internal lead note and alert.</div>
            </div>

            <div class="section-actions">
              <v-btn
                v-if="checkRightStatus(36)"
                small
                rounded
                color="primary"
                dark
                @click="updateNote()"
              >
                <v-icon left small>mdi-content-save</v-icon>
                Save
              </v-btn>
            </div>
          </div>

          <v-card-text class="pa-4">
            <div class="alert-row" v-if="checkRightStatus(37)">
              <span>Alert</span>
              <toggle-button
                @change="updateAlert"
                :labels="true"
                color="red"
                v-model="lead.alert"
                :value="lead.alert"
                :sync="true"
              />
            </div>

            <v-textarea
              v-model="note"
              outlined
              hide-details
              auto-grow
              label="Lead Note"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <Comment :RelationId="lead" :TypeId="13" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import leadService from "../service";
import Comment from "@/modules/Comment/components/List";
import { mapGetters } from "vuex";
const Edit = () => import("../components/Edit");

export default {
  name: "lead-profile",

  components: {
    Comment,
    Edit,
  },

  data() {
    return {
      editDialog: false,
      lead: null,
      selectedLead: null,
      note: null,
    };
  },

  created() {
    this.getLead();
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  methods: {
    openGoogleMap() {
      if (!this.lead.address) return;

      const { address, city, state, zip } = this.lead;
      const query = encodeURIComponent(`${address} ${city}, ${state} ${zip}`);

      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
        "_blank"
      );
    },

    updateAlert(val) {
      this.updateLead({
        alert: val.value,
      });
    },

    updateNote() {
      this.updateLead({
        note: this.note,
      });
    },

    updateLead(data) {
      return leadService.update(this.lead.id, data).then(() => {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Update Successfully",
          });
        this.getLead();
        this.$events.emit("leadListEvent");
      });
    },

    editLead(lead) {
      this.selectedLead = lead;
      this.editDialog = true;
    },

    deleteLead(lead) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          leadService.delete(lead.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Lead has been deleted.", "success");
              this.$events.emit("leadListEvent");
              this.$router.go(-1);
            }
          });
        }
      });
    },

    getLead() {
      const { id } = this.$route.params;

      return leadService.getById(id).then((response) => {
        this.lead = response.data;
        this.note = this.lead ? this.lead.note : null;
        document.title = this.lead
          ? `smplsale - ${this.lead.name}`
          : "smplsale - Lead Profile";
      });
    },

    sendEmail() {
      const routeData =
        "https://mail.google.com/mail/?view=cm&fs=1&to=" + this.lead.email;
      window.open(routeData, "newwindow", "width=800, height=950");
    },

    statusColor(status) {
      if (status === "Hot Lead") return "red lighten-4";
      if (status === "Warm Lead") return "orange lighten-4";
      if (status === "Cold") return "blue lighten-4";
      if (status === "Closed" || status === "Convert") return "green lighten-4";
      if (status === "Dead") return "grey lighten-3";
      return "amber lighten-4";
    },

    statusTextColor(status) {
      if (status === "Hot Lead") return "red darken-4";
      if (status === "Warm Lead") return "orange darken-4";
      if (status === "Cold") return "blue darken-4";
      if (status === "Closed" || status === "Convert") return "green darken-4";
      if (status === "Dead") return "grey darken-3";
      return "amber darken-4";
    },
  },
};
</script>

<style scoped>
.lead-profile-page {
  width: 100%;
}

.lead-profile-card,
.note-card {
  border-radius: 20px;
  background: #ffffff;
}

.profile-header,
.section-header {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  border-bottom: 1px solid #e2e8f0;
}

.profile-title-wrap {
  display: flex;
  align-items: center;
}

.profile-title,
.section-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.profile-subtitle,
.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.profile-actions,
.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.info-item,
.address-card {
  background: #f8fafc;
  border-radius: 16px;
  padding: 12px;
}

.info-label {
  font-size: 11px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.info-value,
.address-line {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.alert-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: 700;
  color: #0f172a;
}

.loading-box {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #64748b;
}

@media (max-width: 600px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .profile-actions,
  .section-actions {
    width: 100%;
  }

  .section-actions .v-btn {
    width: 100%;
  }
}
</style>
